import { useAnalyticsFavorites } from "../lib/analyticsFavorites";
import AnalyticsHeaderSearch from "./AnalyticsHeaderSearch";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, canUseAskAI, isLockedDashboardUser } from "../lib/auth";
import {
  LayoutDashboard,
  Wand2,
  Crown,
  FileText,
  Database,
  Shield,
  Settings,
  Bell,
  UserCircle,
  LogOut,
  Sparkles,
  Boxes,
  Upload,
  Terminal,
  Target,
  Download,
  ScrollText,
  Building2,
  Table2,
  Workflow,
} from "lucide-react";

const ROW_ONE = [
  { id: "home", label: "Home", to: "/", icon: LayoutDashboard },
  { id: "dashboards", label: "Dashboards", to: "/dashboards?view=saved", icon: LayoutDashboard },
  { id: "builder", label: "Builder", to: "/builder/new", icon: Wand2 },
  { id: "ceo", label: "CEO Command Center", to: "/ceo-command-center", icon: Crown },
  { id: "reports", label: "Reports", to: "/reports", icon: FileText },
  { id: "data", label: "Data", to: "/data-sources", icon: Database },
  { id: "admin", label: "Admin", to: "/access-management", icon: Shield },
];

const ROW_TWO = {
  home: [
    { label: "Overview", to: "/?section=overview" },
    { label: "Platform Summary", to: "/?section=platform" },
    { label: "Recent Activity", to: "/?section=activity" },
  ],
  dashboards: [
    { label: "Saved Dashboards", to: "/dashboards?view=saved" },
    { label: "New Dashboard", to: "/builder/new" },
    { label: "Client Dashboards", to: "/dashboards?view=client" },
    { label: "Assignments", to: "/access-management" },
  ],
  builder: [
    { label: "New Dashboard", to: "/builder/new" },
    { label: "Saved Dashboards", to: "/dashboards?view=saved" },
    { label: "Dashboard List", to: "/dashboards?view=saved" },
    { label: "Data Sources", to: "/data-sources" },
    { label: "Datasets", to: "/datasets" },
  ],
  ceo: [
    { label: "Quick Access", to: "/ceo-command-center?tab=quick-access", favoriteCount: true },
    { label: "Dashboard", to: "/ceo-command-center" },
    { label: "Sales", to: "/ceo-command-center?tab=buyer" },
    { label: "Purchase", to: "/ceo-command-center?tab=purchase" },
    { label: "QA", to: "/ceo-command-center?tab=qc" },
    { label: "Process", to: "/ceo-command-center?tab=production" },
    { label: "Store", to: "/ceo-command-center?tab=store" },
    { label: "PO", to: "/ceo-command-center?tab=po" },
    { label: "Dispatch", to: "/ceo-command-center?tab=shipment" },
    { label: "Commercial", to: "/ceo-command-center?tab=commercial" },
    { label: "Comparisons", to: "/ceo-command-center?tab=comparisons" },
    { label: "Master Search", to: "/ceo-command-center?tab=master" },
    { label: "Reports", to: "/reports" },
  ],
  reports: [
    { label: "Reports", to: "/reports", icon: FileText },
    { label: "KPI Center", to: "/kpi", icon: Target },
    { label: "Exports", to: "/exports", icon: Download },
  ],
  data: [
    { label: "Database Explorer", to: "/database-explorer", icon: Table2 },
    { label: "ERP Flow Planner", to: "/erp-flow-planner", icon: Workflow },
    { label: "Data Sources", to: "/data-sources", icon: Database },
    { label: "Datasets", to: "/datasets", icon: Boxes },
    { label: "Excel Uploads", to: "/uploads", icon: Upload },
    { label: "SQL Editor", to: "/sql", icon: Terminal },
  ],
  admin: [
    { label: "Access Management", to: "/access-management", icon: Shield },
    { label: "Workspaces", to: "/workspaces", icon: Building2 },
    { label: "Audit Logs", to: "/audit", icon: ScrollText },
    { label: "Exports", to: "/exports", icon: Download },
  ],
};

function activeMain(pathname) {
  if (pathname.startsWith("/dashboards")) return "dashboards";
  if (pathname.startsWith("/builder")) return "builder";
  if (pathname.startsWith("/ceo-command-center")) return "ceo";
  if (pathname.startsWith("/reports") || pathname.startsWith("/kpi")) return "reports";
  if (pathname.startsWith("/database-explorer") || pathname.startsWith("/erp-flow-planner") || pathname.startsWith("/datasets") || pathname.startsWith("/data-sources") || pathname.startsWith("/uploads") || pathname.startsWith("/sql")) return "data";
  if (pathname.startsWith("/access-management") || pathname.startsWith("/workspaces") || pathname.startsWith("/audit") || pathname.startsWith("/exports")) return "admin";
  return "home";
}

export default function ERPAnalyticsHeader() {
  const { user, logout } = useAuth();
  const { favorites } = useAnalyticsFavorites();
  const location = useLocation();
  const navigate = useNavigate();
  const main = activeMain(location.pathname);
  const rowTwo = ROW_TWO[main] || ROW_TWO.home;
  const [openMenu, setOpenMenu] = useState("");

  const toggleMenu = (name) => {
    setOpenMenu((current) => (current === name ? "" : name));
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const displayRole = String(user?.role || "super_admin").replace(/_/g, " ");

  return (
    <div className="erp-analytics-top">
      <div className="erp-header">
        <div className="erp-company">
          <div className="erp-logo-img">CA</div>
          <div>
            <div className="erp-company-name">Camaroo Analytics</div>
            <div className="erp-powered">Powered By Quantira</div>
          </div>
        </div>

        {user && !isLockedDashboardUser(user) && <AnalyticsHeaderSearch allowAI={canUseAskAI(user)} />}

        <div className="erp-user-area">
          {canUseAskAI(user) && (
            <button
              type="button"
              className="erp-round-btn erp-header-ask-ai"
              onClick={() => window.dispatchEvent(new CustomEvent("quantira:open-ask-ai"))}
            >
              <Sparkles size={16} strokeWidth={1.8} />
              <span>Ask AI</span>
            </button>
          )}
          <div className="erp-menu-anchor">
            <button type="button" className={`erp-round-btn ${openMenu === "settings" ? "active" : ""}`} onClick={() => toggleMenu("settings")}>
              <Settings size={17} strokeWidth={1.8} />
              <span>Settings</span>
            </button>

            {openMenu === "settings" && (
              <div className="erp-header-dropdown settings-panel">
                <div className="erp-dropdown-title">Settings</div>
                <button type="button" onClick={() => navigate("/access-management")}>Access Management</button>
                <button type="button" onClick={() => navigate("/workspaces")}>Workspaces</button>
                <button type="button" onClick={() => navigate("/audit")}>Audit Logs</button>
                <button type="button" onClick={() => navigate("/exports")}>Export History</button>
              </div>
            )}
          </div>

          <div className="erp-menu-anchor">
            <button type="button" className={`erp-icon-btn ${openMenu === "notifications" ? "active" : ""}`} aria-label="Notifications" onClick={() => toggleMenu("notifications")}>
              <Bell size={17} strokeWidth={1.8} />
            </button>

            {openMenu === "notifications" && (
              <div className="erp-header-dropdown notifications-panel">
                <div className="erp-dropdown-title">Notifications</div>
                <div className="erp-empty-note">No new notifications right now.</div>
                <button type="button" onClick={() => navigate("/audit")}>View recent activity</button>
              </div>
            )}
          </div>

          <button type="button" className="erp-user-info erp-profile-trigger" onClick={() => toggleMenu("profile")}>
            <strong>{user?.name || "Super Admin"}</strong>
            <span>{displayRole}</span>
          </button>

          <div className="erp-menu-anchor">
            <button type="button" className={`erp-avatar ${openMenu === "profile" ? "active" : ""}`} onClick={() => toggleMenu("profile")} title="Profile">
              <UserCircle size={31} strokeWidth={1.5} />
            </button>

            {openMenu === "profile" && (
              <div className="erp-header-dropdown profile-panel">
                <div className="erp-dropdown-title">Account</div>
                <div className="erp-profile-card">
                  <strong>{user?.name || "Super Admin"}</strong>
                  <span>{user?.email || "superadmin@camaroo.io"}</span>
                  <small>{displayRole}</small>
                </div>
                <button type="button" onClick={() => navigate("/access-management")}>Manage users</button>
                <button type="button" className="danger" onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>

          <button type="button" className="erp-logout" onClick={handleLogout} title="Logout">
            <LogOut size={15} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      <div className="erp-main-menu compact">
        {ROW_ONE.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.id} to={item.to} className={`erp-main-btn ${main === item.id ? "active" : ""}`}>
              <Icon size={18} strokeWidth={1.8} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>

      <div className="erp-second-menu compact">
        {rowTwo.map((item, index) => {
          const Icon = item.icon;
          const itemUrl = new URL(item.to, window.location.origin);
          const currentTab = new URLSearchParams(location.search).get("tab");
          const itemTab = itemUrl.searchParams.get("tab");

          const itemSearch = itemUrl.search || "";
          const currentSearch = location.search || "";

          const exactPathAndSearch =
            itemUrl.pathname === location.pathname &&
            (itemSearch ? itemSearch === currentSearch : currentSearch === "");

          const isActive =
            main === "home"
              ? (
                  itemUrl.pathname === location.pathname &&
                  ((itemUrl.search || "?section=overview") === (location.search || "?section=overview"))
                )
              : main === "ceo"
                ? (
                      itemUrl.pathname === location.pathname &&
                      ((currentTab || "overview") === (itemTab || "overview"))
                    )
                : (
                    exactPathAndSearch ||
                    (
                      item.to === "/builder/new" &&
                      location.pathname.startsWith("/builder")
                    ) ||
                    (
                      item.to === "/access-management" &&
                      location.pathname.startsWith("/access-management")
                    )
                  );

          return (
            <Link key={`${item.label}-${index}`} to={item.to} className={`erp-pill-btn ${isActive ? "active" : ""}`}>
              {Icon && <Icon size={15} strokeWidth={1.8} />}
              <span>{item.label}{item.favoriteCount ? ` (${favorites.length})` : ""}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
